import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  CalendarCheck,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Heart,
  TrendingUp,
} from "lucide-react";
import { format, subDays, parseISO, startOfDay } from "date-fns";

type Booking = {
  id: string;
  plan: string;
  customer_email: string | null;
  customer_name: string | null;
  status: string;
  amount_cents: number;
  currency: string;
  stripe_session_id: string | null;
  created_at: string;
};

type DailyRevenue = { date: string; revenue: number };

type StatusCount = { name: string; value: number; color: string };

const STATUS_COLORS: Record<string, string> = {
  paid: "#22c55e",
  pending: "#eab308",
  interested: "#3b82f6",
  refunded: "#ef4444",
  cancelled: "#9ca3af",
};

const STATUS_BADGE: Record<string, string> = {
  paid: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  interested: "bg-blue-100 text-blue-800",
  refunded: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-800",
};

function buildDailyRevenue(bookings: Booking[]): DailyRevenue[] {
  const now = new Date();
  const days: DailyRevenue[] = [];
  const revenueMap = new Map<string, number>();

  for (const b of bookings) {
    if (b.status !== "paid") continue;
    const key = format(startOfDay(parseISO(b.created_at)), "yyyy-MM-dd");
    revenueMap.set(key, (revenueMap.get(key) ?? 0) + b.amount_cents / 100);
  }

  for (let i = 29; i >= 0; i--) {
    const key = format(subDays(now, i), "yyyy-MM-dd");
    days.push({ date: key, revenue: revenueMap.get(key) ?? 0 });
  }
  return days;
}

function buildStatusBreakdown(bookings: Booking[]): StatusCount[] {
  const counts: Record<string, number> = {};
  for (const b of bookings) {
    counts[b.status] = (counts[b.status] ?? 0) + 1;
  }
  return Object.entries(counts).map(([name, value]) => ({
    name,
    value,
    color: STATUS_COLORS[name] ?? "#6b7280",
  }));
}

function pctChange(current: number, previous: number): string {
  if (previous === 0) return current > 0 ? "+100%" : "0%";
  const pct = ((current - previous) / previous) * 100;
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(0)}%`;
}

function RevenueTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="text-gray-500">{label ? format(parseISO(label), "MMM d, yyyy") : ""}</p>
      <p className="font-semibold text-gray-900">€{payload[0].value.toFixed(2)}</p>
    </div>
  );
}

function StatsCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-9 w-9 rounded-lg" />
      </div>
      <Skeleton className="h-8 w-20 mb-2" />
      <Skeleton className="h-3 w-16" />
    </div>
  );
}

function ChartSkeleton({ className }: { className?: string }) {
  return (
    <div className={`bg-white rounded-xl border shadow-sm p-5 ${className ?? ""}`}>
      <Skeleton className="h-5 w-32 mb-4" />
      <Skeleton className="h-64 w-full rounded-lg" />
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-5">
      <Skeleton className="h-5 w-40 mb-4" />
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full mb-2 rounded" />
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from("bookings")
          .select("*")
          .order("created_at", { ascending: false });

        if (fetchError) throw fetchError;
        setBookings(data ?? []);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load dashboard data";
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    const thirtyDaysAgo = subDays(now, 30);
    const sixtyDaysAgo = subDays(now, 60);

    const current = bookings.filter((b) => parseISO(b.created_at) >= thirtyDaysAgo);
    const previous = bookings.filter(
      (b) => parseISO(b.created_at) >= sixtyDaysAgo && parseISO(b.created_at) < thirtyDaysAgo
    );

    const count = (list: Booking[], status?: string) =>
      status ? list.filter((b) => b.status === status).length : list.length;

    const revenue = (list: Booking[]) =>
      list.filter((b) => b.status === "paid").reduce((s, b) => s + b.amount_cents, 0) / 100;

    return {
      total: bookings.length,
      totalChange: pctChange(count(current), count(previous)),
      paid: count(bookings, "paid"),
      paidChange: pctChange(count(current, "paid"), count(previous, "paid")),
      interested: count(bookings, "interested"),
      interestedChange: pctChange(count(current, "interested"), count(previous, "interested")),
      refunded: count(bookings, "refunded"),
      refundedChange: pctChange(count(current, "refunded"), count(previous, "refunded")),
      revenue: revenue(bookings),
      revenueChange: pctChange(revenue(current), revenue(previous)),
    };
  }, [bookings]);

  const dailyRevenue = useMemo(() => buildDailyRevenue(bookings), [bookings]);
  const statusBreakdown = useMemo(() => buildStatusBreakdown(bookings), [bookings]);
  const recentBookings = useMemo(() => bookings.slice(0, 5), [bookings]);
  const recentInterest = useMemo(
    () => bookings.filter((b) => b.status === "interested").slice(0, 5),
    [bookings]
  );

  const cards = [
    {
      label: "Total Bookings",
      value: stats.total,
      change: stats.totalChange,
      icon: CalendarCheck,
      iconBg: "bg-blue-100 text-blue-700",
    },
    {
      label: "Paid",
      value: stats.paid,
      change: stats.paidChange,
      icon: CheckCircle,
      iconBg: "bg-green-100 text-green-700",
    },
    {
      label: "Interested",
      value: stats.interested,
      change: stats.interestedChange,
      icon: Heart,
      iconBg: "bg-purple-100 text-purple-700",
    },
    {
      label: "Refunded",
      value: stats.refunded,
      change: stats.refundedChange,
      icon: AlertCircle,
      iconBg: "bg-red-100 text-red-700",
    },
    {
      label: "Revenue",
      value: `€${stats.revenue.toLocaleString("de-DE", { minimumFractionDigits: 2 })}`,
      change: stats.revenueChange,
      icon: DollarSign,
      iconBg: "bg-amber-100 text-amber-700",
    },
  ];

  if (error) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h2>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-500 shrink-0" />
          <div>
            <p className="font-medium text-red-800">Failed to load dashboard</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <TrendingUp className="w-4 h-4" />
          <span>Last 30 days comparison</span>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {loading
          ? Array.from({ length: 5 }).map((_, i) => <StatsCardSkeleton key={i} />)
          : cards.map((c) => {
              const isPositive = c.change.startsWith("+");
              const isNeutral = c.change === "0%";
              return (
                <div key={c.label} className="bg-white rounded-xl border shadow-sm p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-500">{c.label}</span>
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${c.iconBg}`}
                    >
                      <c.icon className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-1">{c.value}</div>
                  <span
                    className={`text-xs font-medium ${
                      isNeutral
                        ? "text-gray-400"
                        : isPositive
                          ? "text-green-600"
                          : "text-red-500"
                    }`}
                  >
                    {c.change} vs prev 30d
                  </span>
                </div>
              );
            })}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {loading ? (
          <>
            <ChartSkeleton className="lg:col-span-2" />
            <ChartSkeleton />
          </>
        ) : (
          <>
            {/* Revenue area chart */}
            <div className="bg-white rounded-xl border shadow-sm p-5 lg:col-span-2">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Daily Revenue (Last 30 Days)
              </h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={dailyRevenue}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v: string) => format(parseISO(v), "MMM d")}
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tickFormatter={(v: number) => `€${v}`}
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    tickLine={false}
                    axisLine={false}
                    width={60}
                  />
                  <Tooltip content={<RevenueTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="#22c55e"
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Status pie chart */}
            <div className="bg-white rounded-xl border shadow-sm p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Status Breakdown</h3>
              {statusBreakdown.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-16">No data yet</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={statusBreakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {statusBreakdown.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number, name: string) => [value, name]}
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                          fontSize: "13px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-3 mt-3 justify-center">
                    {statusBreakdown.map((s) => (
                      <div key={s.name} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: s.color }}
                        />
                        <span className="capitalize">{s.name}</span>
                        <span className="font-medium text-gray-900">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Bottom tables row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <>
            <TableSkeleton />
            <TableSkeleton />
          </>
        ) : (
          <>
            {/* Recent bookings */}
            <div className="bg-white rounded-xl border shadow-sm p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Recent Bookings</h3>
              {recentBookings.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">No bookings yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="pb-2 font-medium">Date</th>
                        <th className="pb-2 font-medium">Customer</th>
                        <th className="pb-2 font-medium">Plan</th>
                        <th className="pb-2 font-medium text-right">Amount</th>
                        <th className="pb-2 font-medium text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentBookings.map((b) => (
                        <tr key={b.id} className="border-b last:border-0">
                          <td className="py-2.5 text-gray-600 whitespace-nowrap">
                            {format(parseISO(b.created_at), "MMM d, yyyy")}
                          </td>
                          <td className="py-2.5 text-gray-900 font-medium">
                            {b.customer_name ?? "—"}
                          </td>
                          <td className="py-2.5 text-gray-600 capitalize">{b.plan}</td>
                          <td className="py-2.5 text-gray-900 text-right">
                            €{(b.amount_cents / 100).toFixed(2)}
                          </td>
                          <td className="py-2.5 text-right">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGE[b.status] ?? "bg-gray-100 text-gray-800"}`}
                            >
                              {b.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Recent interest submissions */}
            <div className="bg-white rounded-xl border shadow-sm p-5">
              <h3 className="text-base font-semibold text-gray-900 mb-4">
                Recent Interest Submissions
              </h3>
              {recentInterest.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">
                  No interest submissions yet
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="pb-2 font-medium">Name</th>
                        <th className="pb-2 font-medium">Email</th>
                        <th className="pb-2 font-medium">Plan</th>
                        <th className="pb-2 font-medium text-right">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentInterest.map((b) => (
                        <tr key={b.id} className="border-b last:border-0">
                          <td className="py-2.5 text-gray-900 font-medium">
                            {b.customer_name ?? "—"}
                          </td>
                          <td className="py-2.5 text-gray-600 truncate max-w-[180px]">
                            {b.customer_email ?? "—"}
                          </td>
                          <td className="py-2.5 text-gray-600 capitalize">{b.plan}</td>
                          <td className="py-2.5 text-gray-600 text-right whitespace-nowrap">
                            {format(parseISO(b.created_at), "MMM d, yyyy")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
